import { InternalFeatureMapFeature, InternalFeatureTag, InternalGameScreenshot } from '../api/screenshot-links'

export const screenshotLinkClassName = 'analyst-review-link'
export const screenshotLinkPluginName = 'screenshotLink'
// The id of the game whose screenshots are offered, set in the editor's init options.
export const screenshotLinkGameIdOption = 'screenshotLinkGameId'

export type ScreenshotLink = {
  screenshotIds: string[]
  marketIso: string
  text: string
}

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// SaaS's `ScreenshotModalLink` reads the market only from `shotModalIso`.
export const buildScreenshotLinkHref = (screenshotIds: string[], marketIso: string) => `/?shotModal=${screenshotIds.join(',')}&shotModalIso=${marketIso}`

export const buildScreenshotLinkHtml = ({ screenshotIds, marketIso, text }: ScreenshotLink) =>
  `<a class="${screenshotLinkClassName}" href="${escapeHtml(buildScreenshotLinkHref(screenshotIds, marketIso))}">${escapeHtml(text)}</a>`

// Also reads legacy's re-edited links, which carry the market as `marketIso`.
export const parseScreenshotLinkHref = (href: string): Omit<ScreenshotLink, 'text'> => {
  const query = href.includes('?') ? href.slice(href.indexOf('?') + 1) : href
  const params = new URLSearchParams(query.replace(/&amp;/g, '&'))
  return {
    screenshotIds: (params.get('shotModal') ?? '').split(',').filter(Boolean),
    marketIso: params.get('shotModalIso') ?? params.get('marketIso') ?? '',
  }
}

export const minFeatureSearchLength = 2

type SearchKeyword = { name: string; linkedIds?: number[] | null }

// The features whose name contains the text, plus those linked to matching keywords and active feature tags.
// Null when the text is too short to filter.
export const getFeatureSearchLegacyIds = (
  text: string,
  features: InternalFeatureMapFeature[],
  keywords: SearchKeyword[],
  tags: InternalFeatureTag[]
): Set<number> | null => {
  const search = text.trim().toLowerCase()
  if (search.length < minFeatureSearchLength) return null
  const ids = new Set<number>()
  features.forEach((feature) => feature.name?.toLowerCase().includes(search) && ids.add(feature.legacyId))
  const tagKeywords: SearchKeyword[] = tags.filter((tag) => tag.active).map((tag) => ({ name: tag.name, linkedIds: tag.targets }))
  ;[...keywords, ...tagKeywords].forEach((keyword) => {
    if (keyword.name?.toLowerCase().includes(search)) keyword.linkedIds?.forEach((id) => ids.add(id))
  })
  return ids
}

// The legacy ids targeted by any of the selected tags. Null when no tag is selected.
export const getSelectedTagLegacyIds = (tags: InternalFeatureTag[], selectedTagIds: string[]): Set<number> | null => {
  if (!selectedTagIds.length) return null
  return new Set(tags.filter((tag) => selectedTagIds.includes(tag.id)).flatMap((tag) => tag.targets ?? []))
}

const hasAnyFeature = (screenshot: InternalGameScreenshot, legacyIds: Set<number>) =>
  Object.keys(screenshot.features ?? {}).some((legacyId) => legacyIds.has(Number(legacyId)))

export type ScreenshotFilters = {
  marketIso: string
  tagLegacyIds: Set<number> | null
  searchLegacyIds: Set<number> | null
  onlyIds: Set<string> | null
}

// Newest version first.
export const filterScreenshots = (screenshots: InternalGameScreenshot[], { marketIso, tagLegacyIds, searchLegacyIds, onlyIds }: ScreenshotFilters) =>
  screenshots
    .filter((screenshot) => (screenshot.markets ?? []).includes(marketIso))
    .filter((screenshot) => !onlyIds || onlyIds.has(screenshot.id))
    .filter((screenshot) => !tagLegacyIds || hasAnyFeature(screenshot, tagLegacyIds))
    .filter((screenshot) => !searchLegacyIds || hasAnyFeature(screenshot, searchLegacyIds))
    .sort((a, b) => (b.versionReleaseDate ?? 0) - (a.versionReleaseDate ?? 0))

export const describeScreenshotFeatures = (screenshot: InternalGameScreenshot, featuresByLegacyId: Map<number, InternalFeatureMapFeature>) =>
  Object.entries(screenshot.features ?? {}).map(([legacyId, weightId]) => {
    const feature = featuresByLegacyId.get(Number(legacyId))
    const name = feature?.name ?? legacyId
    if (!weightId) return name
    const choice = feature?.choices?.find((candidate) => candidate.weightId === weightId)?.choice
    return `${name}: ${choice ?? weightId}`
  })

// Markets that have screenshots, in the markets list's order.
// Sorted by name: the markets endpoint answers in no fixed order.
export const getScreenshotMarkets = <T extends { iso: string; name?: string | null }>(screenshots: InternalGameScreenshot[], markets: T[]) => {
  const isos = new Set(screenshots.flatMap((screenshot) => screenshot.markets ?? []))
  return markets.filter((market) => isos.has(market.iso)).sort((a, b) => (a.name ?? a.iso).localeCompare(b.name ?? b.iso))
}

// The edited link's market, else the game's first market with screenshots, else the first market with screenshots.
export const getDefaultScreenshotMarketIso = (marketIsos: string[], gameMarketIsos: string[], linkMarketIso?: string) => {
  if (linkMarketIso && marketIsos.includes(linkMarketIso)) return linkMarketIso
  return gameMarketIsos.find((iso) => marketIsos.includes(iso)) ?? marketIsos[0] ?? ''
}
